import { useMemo } from "react";
import { CircularProgress } from "@mui/material";
import { debug as debugFn } from "debug";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { v4 } from "uuid";

import type {
  IncidentWorkItemWithContext,
  WorkflowVersion,
} from "@thalamos/vision-common";
import {
  IncidentFormIdSchema,
  IncidentWorkItemIdSchema,
} from "@thalamos/vision-common";

import api, { apiHooks } from "../../api/index.js";
import {
  RecordPlaceOfSafetyForm,
  RecordPlaceOfSafetyFormSkeleton,
} from "../../forms/RecordPlaceOfSafety/RecordPlaceOfSafetyForm.js";
import { useToastNotifications } from "../../hooks/useToastNotifications.js";
import { Error as ErrorComponent } from "../../pages/Error/Error.js";
import { createRoute } from "../../routing/createRoute.js";

const debug = debugFn("vision-frontend:RecordPlaceOfSafetyPage");

export type RecordPlaceOfSafetyPageProps = Record<string, unknown>;

export type RecordPlaceOfSafetyPageInnerProps = {
  incidentWorkItem: IncidentWorkItemWithContext | undefined;
  refetch: () => Promise<void>;
};

export function RecordPlaceOfSafetyPage(_props: RecordPlaceOfSafetyPageProps) {
  const params = useParams();

  const incidentWorkItemId = IncidentWorkItemIdSchema.safeParse(params.id);
  if (!incidentWorkItemId.success) {
    throw new Error("Invalid incident work item id");
  }

  const [
    {
      data: incidentWorkItem,
      loading: isIncidentLoading,
      error: incidentError,
      response: incidentResponse,
    },
    refetchIncidentWorkItem,
  ] = apiHooks.incidentWorkItem.get(incidentWorkItemId.data);

  if (incidentError) {
    return <ErrorComponent />;
  }

  // `apiAxios` answers a 401 by navigating to the login page, so wait for it.
  if (isIncidentLoading || incidentResponse?.status === 401) {
    return <CircularProgress />;
  }

  if (incidentResponse?.status !== 200) {
    return <ErrorComponent />;
  }

  return (
    <RecordPlaceOfSafetyPageInner
      incidentWorkItem={incidentWorkItem ?? undefined}
      refetch={async () => {
        await refetchIncidentWorkItem();
      }}
    />
  );
}

export function RecordPlaceOfSafetyPageInner({
  incidentWorkItem,
  refetch,
}: RecordPlaceOfSafetyPageInnerProps) {
  const navigate = useNavigate();
  const toastNotifications = useToastNotifications();
  const { t } = useTranslation();

  const formId = useMemo(() => IncidentFormIdSchema.parse(v4()), []);

  const render = (
    incidentWorkItem: IncidentWorkItemWithContext | undefined,
  ) => {
    const incidentWorkItemId = incidentWorkItem?.incidentWorkItem.id;
    const workflowVersion = incidentWorkItem?.incidentWorkItem
      .workflowVersion as WorkflowVersion | undefined;
    return (
      <>
        {incidentWorkItemId === undefined || workflowVersion === undefined ? (
          <RecordPlaceOfSafetyFormSkeleton />
        ) : (
          <RecordPlaceOfSafetyForm
            initialValues={
              // This is always a create form, so there are never initial values
              undefined
            }
            onAutoSave={
              // Because completing this form has a side effect of creating a new POS,
              // we don't want to auto save it, but wait for the submit at the end.
              undefined
            }
            onSubmit={async (values: Record<string, unknown>) => {
              try {
                const response =
                  await api.incidentWorkItem.createAndPopulatePlaceOfSafety(
                    incidentWorkItemId,
                    formId,
                    values,
                  );
                if (response.status !== 204) {
                  throw Error(JSON.stringify(response.data.message));
                }

                await refetch();
                navigate(
                  createRoute.incidentRecordPlaceOfSafetyArrivalPage(
                    incidentWorkItemId,
                    formId,
                  ),
                );
                return;
              } catch (error) {
                debug("Failed to record place of safety", error);
                toastNotifications.show(
                  t("incidentWorkItem.notificationFailedToSave"),
                  { severity: "error" },
                );
              }
            }}
            onCancel={() => {
              navigate(createRoute.incidentProgressPage(incidentWorkItemId));
            }}
            workflowVersion={workflowVersion}
            incidentWorkItem={incidentWorkItem}
          />
        )}
      </>
    );
  };

  return render(incidentWorkItem);
}
